/**
 * Keyboard Navigation Composable
 * 
 * Shared keyboard navigation logic for mention suggestion lists.
 * Handles Arrow Up/Down, Enter, and Escape keys.
 */

import { ref, watch, type Ref } from 'vue'

export const useKeyboardNavigation = <T>(
  items: Ref<T[]>,
  onSelect: (item: T) => void,
  onClose?: () => void
) => {
  const selectedIndex = ref(0)

  // Reset selected index when items change
  watch(items, () => {
    selectedIndex.value = 0
  })

  /**
   * Navigate to previous item (Arrow Up)
   */
  const navigateUp = () => {
    if (selectedIndex.value > 0) {
      selectedIndex.value--
    } else {
      // Wrap to last item
      selectedIndex.value = items.value.length - 1
    }
  }

  /**
   * Navigate to next item (Arrow Down)
   */
  const navigateDown = () => {
    if (selectedIndex.value < items.value.length - 1) {
      selectedIndex.value++
    } else {
      // Wrap to first item
      selectedIndex.value = 0
    }
  }

  /**
   * Select current item (Enter)
   */
  const selectCurrent = () => {
    const currentItem = items.value[selectedIndex.value]
    if (currentItem) {
      onSelect(currentItem)
    }
  }

  /**
   * Handle keyboard event
   */
  const handleKeyDown = (event: KeyboardEvent): boolean => {
    // Only handle if we have items
    if (items.value.length === 0) {
      return false
    }

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        navigateUp()
        return true

      case 'ArrowDown':
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        navigateDown()
        return true

      case 'Enter':
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        selectCurrent()
        return true

      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        if (onClose) {
          onClose()
        }
        return true

      default:
        return false
    }
  }

  /**
   * Set selected index by mouse hover
   */
  const setSelectedIndex = (index: number) => {
    selectedIndex.value = index
  }

  return {
    selectedIndex,
    navigateUp,
    navigateDown,
    selectCurrent,
    handleKeyDown,
    setSelectedIndex,
  }
}

